// ── Friet Fighter — Lab / preview-pagina ──
// Dev-tool (geen onderdeel van de game): bekijk elk karakter individueel, orbit
// de camera, speel elke animatie-clip, trigger impact-FX en vergelijk met de 2D-
// canonical. Per-fighter finetune (schaal/yaw/hoogte) met "copy config" zodat we
// de waarden later in de game kunnen bakken.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { ImpactFX } from './render/fx';
import { actionDeltas, actionSquash } from './render/poses';
import { FIGHTERS, BOSSES_BY_ID, finetuneFor, type FighterDef, type Finetune } from './config/fighters';
import { signatureFor, specialsFor, superFor } from './sim/movesets';
import { CINEMATICS, swingPoseAt, hasCinematic } from './render/cinematic';
import { CinematicFxRunner } from './render/cinematicFx';

// ── Move-lijst per karakter ──────────────────────────────────────────────────
// We tonen NIET langer één platte lijst van álle 40 acties voor elk karakter
// (dat verstopte de eigen moves tussen die van de andere 8). In plaats daarvan
// is de movelist contextueel: gedeelde basis + de EIGEN specials + EIGEN super,
// afgeleid uit de sim-moveset (movesets.ts is de single source).

interface LabMove { label: string; id: string }

// Gedeelde normalen — identiek voor elk karakter (de "basis").
const UNIVERSAL: LabMove[] = [
  { label: 'Idle', id: 'idle' },
  { label: 'Loop', id: 'walk' },
  { label: 'Hurk', id: 'crouch' },
  { label: 'Block', id: 'block' },
  { label: 'Sprong', id: 'jump' },
  { label: 'Jab', id: 'standLight' },
  { label: 'Cross', id: 'standHeavy' },
  { label: 'Lage poke', id: 'crouchLight' },
  { label: 'Trap', id: 'kick' },
  { label: 'Hit', id: 'hitstun' },
  { label: 'K.O.', id: 'ko' },
];

/** De eigen specials van een vechter (signature gemarkeerd) + zijn super. */
function ownMoves(charId: string): { specials: (LabMove & { sig: boolean })[]; sup: LabMove | null } {
  const sigId = signatureFor(charId)?.id;
  const specials = specialsFor(charId).map((m) => ({ label: m.name, id: m.id, sig: m.id === sigId }));
  const s = superFor(charId);
  return { specials, sup: s ? { label: s.name, id: s.id } : null };
}

/** Leesbaar label voor een actie-id (basis óf de eigen move van het karakter). */
function labelFor(charId: string, id: string): string {
  const u = UNIVERSAL.find((a) => a.id === id);
  if (u) return u.label;
  const own = ownMoves(charId);
  return own.specials.find((m) => m.id === id)?.label ?? (own.sup?.id === id ? own.sup.label : id);
}

// Roster + finetune-defaults komen uit de gedeelde config (zie config/fighters.ts).
// Het lab toont de hele cast — inclusief de boss. Die leeft buiten FIGHTERS[] (dus
// niet in het game-select-grid) maar moet hier wél inspecteerbaar zijn.
const BOSSES: FighterDef[] = Object.values(BOSSES_BY_ID);
const ROSTER: FighterDef[] = [...FIGHTERS, ...BOSSES];

const TARGET_H = 1.8;
const BASE = import.meta.env.BASE_URL;

const DEFAULT_FT: Finetune = { scale: 1, yaw: 0, yOffset: 0 };
const finetunes = new Map<string, Finetune>();

// ── CSS (inline zodat we de game-css niet raken) ──
const css = `
  * { box-sizing: border-box; }
  html, body { margin: 0; height: 100%; background: #14100c; color: #f3e9d8;
    font-family: ui-sans-serif, system-ui, sans-serif; overflow: hidden; }
  #lab { display: grid; grid-template-columns: 220px 1fr 300px; height: 100vh; }
  #roster { background: #1c1611; border-right: 1px solid #34281c; overflow-y: auto; padding: 10px; }
  #roster h2, #panel h2 { font-size: 13px; text-transform: uppercase; letter-spacing: .08em;
    color: #c79a5b; margin: 6px 4px 10px; }
  .fbtn { display: flex; align-items: center; gap: 9px; width: 100%; text-align: left;
    background: #241b13; border: 1px solid #34281c; color: #f3e9d8; padding: 9px 10px;
    border-radius: 8px; margin-bottom: 6px; cursor: pointer; font-size: 14px; }
  .fbtn:hover { background: #2f231799; border-color: #6b4f2e; }
  .fbtn.active { background: #4a3416; border-color: #e0a740; }
  .dot { width: 12px; height: 12px; border-radius: 50%; flex: 0 0 auto;
    border: 1px solid #0006; }
  .fbtn small { color: #b59a72; display: block; font-size: 11px; }
  #stage { position: relative; }
  #view { width: 100%; height: 100%; display: block; }
  #overlay { position: absolute; left: 14px; top: 12px; font-size: 13px; color: #ffe6b0;
    text-shadow: 0 1px 3px #000; pointer-events: none; }
  #overlay b { font-size: 18px; }
  #panel { background: #1c1611; border-left: 1px solid #34281c; overflow-y: auto; padding: 12px; }
  .movesec { margin-bottom: 12px; }
  .movesec h3 { font-size: 10px; text-transform: uppercase; letter-spacing: .1em; font-weight: 600;
    color: #8a7657; margin: 0 0 6px; display: flex; align-items: center; gap: 6px; }
  .movesec h3::after { content: ''; flex: 1; height: 1px; background: #34281c; }
  .clips { display: flex; flex-wrap: wrap; gap: 6px; }
  .clip { background: #241b13; border: 1px solid #34281c; color: #f3e9d8; padding: 6px 9px;
    border-radius: 6px; cursor: pointer; font-size: 12px; }
  .clip:hover { border-color: #6b4f2e; }
  .clip.active { background: #4a3416; border-color: #e0a740; color: #ffe6b0; }
  /* Specials krijgen het karakter-accent; de super valt op in goud. */
  .clip.special { border-color: #6b4f2e; }
  .clip.special.active { background: #4a3416; }
  .clip.super { background: #3a2a14; border-color: #c79a5b; color: #ffe6b0; font-weight: 600; }
  .clip.super.active { background: #6b4a1a; border-color: #ffd27a; }
  .row { display: flex; align-items: center; gap: 8px; margin: 8px 0; font-size: 12px; }
  .row label { flex: 0 0 70px; color: #b59a72; }
  .row input[type=range] { flex: 1; }
  .row .val { flex: 0 0 46px; text-align: right; color: #ffe6b0; font-variant-numeric: tabular-nums; }
  .act { display: flex; gap: 8px; flex-wrap: wrap; margin: 10px 0; }
  .act button { background: #3a2a14; border: 1px solid #6b4f2e; color: #ffe6b0; padding: 8px 11px;
    border-radius: 7px; cursor: pointer; font-size: 13px; }
  .act button:hover { background: #4a3416; }
  #concept { width: 100%; border-radius: 8px; border: 1px solid #34281c; margin-top: 6px; background:#0a0805; }
  #cfg { width: 100%; height: 60px; background: #0d0a07; color: #9fd29f; border: 1px solid #34281c;
    border-radius: 6px; font-family: ui-monospace, monospace; font-size: 11px; margin-top: 6px; }
  .hint { color: #8a7657; font-size: 11px; margin: 4px 0 10px; }
`;
const styleEl = document.createElement('style');
styleEl.textContent = css;
document.head.appendChild(styleEl);

// ── Three.js scene ──
const canvas = document.querySelector<HTMLCanvasElement>('#view')!;
const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x14100c);

const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
camera.position.set(0, 1.05, 3.6);

const controls = new OrbitControls(camera, canvas);
controls.target.set(0, 0.9, 0);
controls.enableDamping = true;
controls.minDistance = 1.5;
controls.maxDistance = 12;

// Studio-belichting (neutraal, toont de echte texture-look).
scene.add(new THREE.AmbientLight(0xfff3e0, 0.9));
const key = new THREE.DirectionalLight(0xffffff, 1.1);
key.position.set(3, 6, 5);
scene.add(key);
const fill = new THREE.DirectionalLight(0xbfd4ff, 0.4);
fill.position.set(-4, 2, -3);
scene.add(fill);
const rim = new THREE.DirectionalLight(0xffd9a0, 0.5);
rim.position.set(0, 3, -6);
scene.add(rim);

// Vloer + grid voor schaalbesef.
const grid = new THREE.GridHelper(10, 20, 0x6b4f2e, 0x34281c);
(grid.material as THREE.Material).transparent = true;
(grid.material as THREE.Material).opacity = 0.5;
scene.add(grid);
const floor = new THREE.Mesh(
  new THREE.CircleGeometry(3, 40),
  new THREE.MeshStandardMaterial({ color: 0x241b13, roughness: 0.9 }),
);
floor.rotation.x = -Math.PI / 2;
floor.position.y = -0.001;
scene.add(floor);

const fx = new ImpactFX();
scene.add(fx.group);

// ── Karakter-state ──
const holder = new THREE.Group(); // user-finetune transforms
scene.add(holder);
const inner = new THREE.Group(); // genormaliseerde mesh
holder.add(inner);

// Volledig procedureel: de mocap-clips passen niet op stompe snacks. We poseren
// per frame vanuit de rust-houding met de gedeelde ACTIONS (poses.ts).
let currentAction = 'idle';
let actionTime = 0; // sec sinds actie-start (schaalt met snelheid)
let speed = 1;
let autoRotate = false;
let polycount = 0;
let clipCount = 0;
let active: FighterDef = ROSTER[0];

// Cinematic-preview (super-cutscene): speelt de bespoke swing + prop uit cinematic.ts
// in de lab-scene af, zodat we 'm direct kunnen beoordelen. Loop windup→steek→follow.
let cineMode = false;
let cineClock = 0;
let cineU = 0;
let prikker: THREE.Object3D | null = null;
let fxRunner: CinematicFxRunner | null = null;
const CINE_SWING = 1.4; // s voor de swing zelf
const CINE_HOLD = 0.5; // s hold op follow-through vóór de loop herstart
const LAB_PROP_K = 1.4; // lab-fighter is groter dan in de game → prop iets opschalen
const cineHand = new THREE.Vector3();
const cineElbow = new THREE.Vector3();
const cineDir = new THREE.Vector3();
const cineLocalX = new THREE.Vector3(1, 0, 0);
const cineTip = new THREE.Vector3();
const cineOrigin = new THREE.Vector3();

function exitCine(): void {
  cineMode = false;
  if (prikker) {
    scene.remove(prikker);
    prikker = null;
  }
  if (fxRunner) {
    fxRunner.clear();
    scene.remove(fxRunner.group);
    fxRunner = null;
  }
  // Herstel de standaard lab-framing.
  camera.position.set(0, 1.05, 3.6);
  controls.target.set(0, 0.9, 0);
  controls.update();
}

function enterCine(): void {
  const def = CINEMATICS[active.id];
  if (!def) return;
  exitCine();
  cineMode = true;
  cineClock = 0;
  cineU = 0;
  if (def.prop) {
    prikker = def.prop.build();
    prikker.scale.setScalar(def.prop.scale * LAB_PROP_K);
    scene.add(prikker);
  }
  fxRunner = def.fx ? new CinematicFxRunner(def.fx) : null;
  if (fxRunner) scene.add(fxRunner.group);
  // Zoom uit zodat de hele swing-arc (incl. uitgestoken prop) in beeld past;
  // orbit blijft werken (gebruiker kan vrij ronddraaien vanaf hier).
  camera.position.set(2.0, 1.6, 6.2);
  controls.target.set(0, 1.0, 0);
  controls.update();
}

const loader = new GLTFLoader();
const clock = new THREE.Clock();
const restQuats = new Map<THREE.Bone, THREE.Quaternion>();
const boneByName = new Map<string, THREE.Bone>();
const poseQ = new THREE.Quaternion();
const poseAxis = new THREE.Vector3();

// Kalibratie-hook: window.__labPose([{bone,axis,angle}]) → bevries rust ⊕ delta's.
let debugPose: { bone: string; axis: [number, number, number]; angle: number }[] | null = null;
const dbgQ = new THREE.Quaternion();
const dbgAxis = new THREE.Vector3();
(window as unknown as { __labPose: (d: unknown) => void }).__labPose = (d) => {
  debugPose = (d as never) || null;
};

/** Normaliseer mesh: schaal naar TARGET_H, centreer x/z, voeten op y=0. */
function normalize(obj: THREE.Object3D): void {
  obj.updateMatrixWorld(true);
  const size = new THREE.Box3().setFromObject(obj).getSize(new THREE.Vector3());
  const s = size.y > 1e-4 ? TARGET_H / size.y : 1;
  obj.scale.setScalar(s);
  obj.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(obj);
  obj.position.x -= (box.min.x + box.max.x) / 2;
  obj.position.z -= (box.min.z + box.max.z) / 2;
  obj.position.y -= box.min.y;
}

function applyFinetune(): void {
  const ft = finetunes.get(active.id) ?? DEFAULT_FT;
  holder.scale.setScalar(ft.scale);
  holder.rotation.y = (ft.yaw * Math.PI) / 180;
  // y-positie wordt elke frame door de grounding gezet (zie ground()).
}

// Houd de voeten op de vloer ondanks dat mocap-clips de heupen verticaal bewegen:
// meet de echte (geanimeerde) bounding-box en grond het laagste punt op de y-offset.
const groundBox = new THREE.Box3();
// Plant het LAAGSTE mesh-punt exact op de vloer (yOffset). Dit kan per definitie
// niet door de vloer steken. Tijdens loop-animaties die de voeten optillen
// (walk/jump) blijft het laagste contactpunt netjes op de grond.
function ground(): void {
  if (inner.children.length === 0) return;
  const ft = finetunes.get(active.id) ?? DEFAULT_FT;
  holder.position.y = 0;
  holder.updateMatrixWorld(true);
  groundBox.setFromObject(inner);
  if (Number.isFinite(groundBox.min.y)) holder.position.y = ft.yOffset - groundBox.min.y;
}

async function loadFighter(def: FighterDef): Promise<void> {
  exitCine(); // prop van de vorige vechter opruimen bij een switch
  active = def;
  if (!finetunes.has(def.id)) finetunes.set(def.id, finetuneFor(def.id));
  inner.clear();
  polycount = 0;
  clipCount = 0;

  const url = `${BASE}models/${def.id}.glb`;
  const gltf = await loader.loadAsync(url).catch(() => null);
  if (!gltf) {
    overlay.innerHTML = `<b>${def.name}</b><br><span style="color:#e87">model ontbreekt: ${url}</span>`;
    return;
  }
  normalize(gltf.scene);
  clipCount = gltf.animations.length;
  gltf.scene.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh && m.geometry) polycount += m.geometry.attributes.position?.count ?? 0;
  });
  inner.add(gltf.scene);

  // Leg de rust-pose vast → basis voor alle procedurele poses.
  restQuats.clear();
  boneByName.clear();
  gltf.scene.traverse((o) => {
    const bone = o as THREE.Bone;
    if (bone.isBone) {
      restQuats.set(bone, bone.quaternion.clone());
      boneByName.set(bone.name, bone);
    }
  });

  applyFinetune();
  setAction('idle');
  buildPanel();
  buildRoster();
}

/** Pose de huidige procedurele actie op actionTime (vanuit de rust-houding). */
function poseAction(): void {
  // cartoon-squash (Y-schaal); X/Z compenseren voor ~volumebehoud
  const sq = actionSquash(currentAction);
  inner.scale.set(1 / Math.sqrt(sq), sq, 1 / Math.sqrt(sq));
  for (const [bone, q] of restQuats) bone.quaternion.copy(q);
  for (const d of actionDeltas(currentAction, actionTime)) {
    const b = boneByName.get(d.bone);
    if (!b) continue;
    poseAxis.set(d.axis[0], d.axis[1], d.axis[2]);
    poseQ.setFromAxisAngle(poseAxis, d.angle);
    b.quaternion.multiply(poseQ);
  }
}

/** Pose de cinematic-swing op fase u (vanuit rust), met de cinematic-squash. */
function poseCine(u: number): void {
  const def = CINEMATICS[active.id];
  if (!def) {
    exitCine();
    return;
  }
  const sq = def.squash;
  inner.scale.set(1 / Math.sqrt(sq), sq, 1 / Math.sqrt(sq));
  for (const [bone, q] of restQuats) bone.quaternion.copy(q);
  for (const d of swingPoseAt(def.swing, u)) {
    const b = boneByName.get(d.bone);
    if (!b) continue;
    poseAxis.set(d.axis[0], d.axis[1], d.axis[2]);
    poseQ.setFromAxisAngle(poseAxis, d.angle);
    b.quaternion.multiply(poseQ);
  }
}

/** Richt de prikker (wereld) langs de onderarm — zelfde logica als de game-director. */
function placePrikker(): void {
  const hand = boneByName.get('RightHand');
  const elbow = boneByName.get('RightForeArm');
  if (!hand || !elbow || !prikker) return;
  hand.getWorldPosition(cineHand);
  elbow.getWorldPosition(cineElbow);
  cineDir.copy(cineHand).sub(cineElbow);
  if (cineDir.lengthSq() < 1e-6) return;
  cineDir.normalize();
  prikker.position.copy(cineHand).addScaledVector(cineDir, 0.42 * LAB_PROP_K);
  prikker.quaternion.setFromUnitVectors(cineLocalX, cineDir);
}

function setAction(id: string): void {
  exitCine(); // een gewone move kiezen verlaat de cinematic-preview
  currentAction = id;
  actionTime = 0;
  buildPanel();
  updateOverlay();
}

function updateOverlay(): void {
  const label = labelFor(active.id, currentAction);
  overlay.innerHTML =
    `<b>${active.name}</b> &nbsp;<span style="color:#c79a5b">${active.archetype}</span><br>` +
    `actie: ${label} (procedureel) · ${clipCount} mocap-clips · ${polycount.toLocaleString('nl')} verts`;
}

// ── UI ──
const roster = document.querySelector<HTMLElement>('#roster')!;
const panel = document.querySelector<HTMLElement>('#panel')!;
const overlay = document.querySelector<HTMLElement>('#overlay')!;

function buildRoster(): void {
  roster.innerHTML = '';
  const addGroup = (title: string, fighters: FighterDef[]): void => {
    if (fighters.length === 0) return;
    const h = document.createElement('h2');
    h.textContent = title;
    roster.appendChild(h);
    for (const f of fighters) {
      const b = document.createElement('button');
      b.className = 'fbtn' + (f.id === active.id ? ' active' : '');
      b.innerHTML =
        `<span class="dot" style="background:${f.glove}"></span>` +
        `<span>${f.name}<small>${f.archetype}</small></span>`;
      b.onclick = () => void loadFighter(f);
      roster.appendChild(b);
    }
  };
  addGroup('Roster', FIGHTERS);
  addGroup('Boss', BOSSES);
}

function slider(
  label: string,
  min: number,
  max: number,
  step: number,
  get: () => number,
  set: (v: number) => void,
  fmt: (v: number) => string,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'row';
  const val = document.createElement('span');
  val.className = 'val';
  val.textContent = fmt(get());
  const input = document.createElement('input');
  input.type = 'range';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(get());
  input.oninput = () => {
    set(parseFloat(input.value));
    val.textContent = fmt(get());
    applyFinetune();
    cfg.value = JSON.stringify({ id: active.id, ...finetunes.get(active.id) });
  };
  const lab = document.createElement('label');
  lab.textContent = label;
  row.append(lab, input, val);
  return row;
}

const cfg = document.createElement('textarea');
cfg.id = 'cfg';
cfg.readOnly = true;

/** Eén move-sectie (kopje + knoppen) met een optionele extra clip-class. */
function moveSection(title: string, moves: LabMove[], extra = ''): HTMLElement | null {
  if (moves.length === 0) return null;
  const sec = document.createElement('div');
  sec.className = 'movesec';
  const h = document.createElement('h3');
  h.textContent = title;
  sec.appendChild(h);
  const clipWrap = document.createElement('div');
  clipWrap.className = 'clips';
  for (const a of moves) {
    const b = document.createElement('button');
    b.className = ('clip ' + extra + (a.id === currentAction ? ' active' : '')).trim();
    b.textContent = a.label;
    b.onclick = () => setAction(a.id);
    clipWrap.appendChild(b);
  }
  sec.appendChild(clipWrap);
  return sec;
}

function buildPanel(): void {
  panel.innerHTML = '<h2>Moves</h2>';
  const own = ownMoves(active.id);
  // Basis (gedeeld) → eigen Specials → eigen Super. Alleen de moves van DIT
  // karakter, zodat zijn unieke kit meteen zichtbaar is.
  const sections = [
    moveSection('Basis', UNIVERSAL),
    moveSection('Specials', own.specials.map((m) => ({ label: m.label, id: m.id })), 'special'),
    moveSection('Super — Vetslag', own.sup ? [own.sup] : [], 'super'),
  ];
  for (const s of sections) if (s) panel.appendChild(s);

  // Cinematic (super-cutscene): speelt de bespoke swing + prop, alleen voor vechters
  // die er een hebben (nu Berenhap). Loopt zodat je 'm kunt orbiten en beoordelen.
  if (hasCinematic(active.id)) {
    const sec = document.createElement('div');
    sec.className = 'movesec';
    const h = document.createElement('h3');
    h.textContent = 'Cinematic (super)';
    sec.appendChild(h);
    const wrap = document.createElement('div');
    wrap.className = 'clips';
    const btn = document.createElement('button');
    btn.className = 'clip super' + (cineMode ? ' active' : '');
    btn.textContent = cineMode ? 'Cinematic ▮ stop' : 'Speel cinematic ▶';
    btn.onclick = () => {
      if (cineMode) exitCine();
      else enterCine();
      buildPanel();
    };
    wrap.appendChild(btn);
    sec.appendChild(wrap);
    panel.appendChild(sec);
  }

  // playback-controls
  const act = document.createElement('div');
  act.className = 'act';
  const rotBtn = document.createElement('button');
  rotBtn.textContent = 'Turntable';
  rotBtn.onclick = () => {
    autoRotate = !autoRotate;
    rotBtn.style.background = autoRotate ? '#4a3416' : '';
  };
  const fxBtn = document.createElement('button');
  fxBtn.textContent = '💥 Impact-FX';
  fxBtn.onclick = () => fx.spawn(0, 1.1, 1.2, 0xffdd55);
  const fxKoBtn = document.createElement('button');
  fxKoBtn.textContent = 'KO-FX';
  fxKoBtn.onclick = () => fx.spawn(0, 1.1, 2.2, 0xff5533);
  act.append(rotBtn, fxBtn, fxKoBtn);
  panel.appendChild(act);

  // speed (schaalt de procedurele actie-tijd)
  panel.appendChild(
    slider('Snelheid', 0.1, 2, 0.05, () => speed, (v) => { speed = v; }, (v) => v.toFixed(2) + '×'),
  );

  // finetune
  const h = document.createElement('h2');
  h.textContent = 'Finetune';
  panel.appendChild(h);
  const ft = () => finetunes.get(active.id)!;
  panel.appendChild(slider('Schaal', 0.4, 2.5, 0.01, () => ft().scale, (v) => (ft().scale = v), (v) => v.toFixed(2)));
  panel.appendChild(slider('Yaw°', -180, 180, 1, () => ft().yaw, (v) => (ft().yaw = v), (v) => v.toFixed(0)));
  panel.appendChild(slider('Y-offset', -0.5, 0.5, 0.01, () => ft().yOffset, (v) => (ft().yOffset = v), (v) => v.toFixed(2)));
  const reset = document.createElement('div');
  reset.className = 'act';
  const rb = document.createElement('button');
  rb.textContent = 'Reset finetune';
  rb.onclick = () => { finetunes.set(active.id, finetuneFor(active.id)); applyFinetune(); buildPanel(); };
  reset.appendChild(rb);
  panel.appendChild(reset);
  cfg.value = JSON.stringify({ id: active.id, ...finetunes.get(active.id) });
  panel.appendChild(cfg);

  // concept-vergelijk
  const ch = document.createElement('h2');
  ch.textContent = '2D-canonical';
  panel.appendChild(ch);
  const img = document.createElement('img');
  img.id = 'concept';
  img.src = `${BASE}concepts/${active.id}-mascot-A.jpeg`;
  img.alt = active.name;
  panel.appendChild(img);

  updateOverlay();
}

// ── Resize + loop ──
function resize(): void {
  const w = canvas.clientWidth || 1;
  const h = canvas.clientHeight || 1;
  renderer.setSize(w, h, false);
  camera.aspect = w / h;
  camera.updateProjectionMatrix();
}
window.addEventListener('resize', resize);

function frame(): void {
  const dt = Math.min(0.05, clock.getDelta());
  actionTime += dt * speed;
  if (autoRotate) holder.rotation.y += dt * 0.6;
  if (restQuats.size) {
    if (debugPose) {
      inner.scale.set(1, 1, 1);
      for (const [bone, q] of restQuats) bone.quaternion.copy(q);
      for (const d of debugPose) {
        const b = boneByName.get(d.bone);
        if (!b) continue;
        dbgAxis.set(d.axis[0], d.axis[1], d.axis[2]);
        dbgQ.setFromAxisAngle(dbgAxis, d.angle);
        b.quaternion.multiply(dbgQ);
      }
    } else if (cineMode) {
      cineClock += dt * speed;
      const tc = cineClock % (CINE_SWING + CINE_HOLD);
      cineU = tc < CINE_SWING ? tc / CINE_SWING : 1; // swing → hold op follow → herstart
      poseCine(cineU);
    } else poseAction();
  }
  ground();
  if (cineMode && prikker) placePrikker();
  if (cineMode && fxRunner) {
    let tip: THREE.Vector3 | null = null;
    const tb = CINEMATICS[active.id]?.fx?.trail?.bone;
    const tbBone = tb ? boneByName.get(tb) : undefined;
    if (tbBone) tip = (tbBone.getWorldPosition(cineTip), cineTip);
    else if (prikker) tip = cineTip.copy(cineHand).addScaledVector(cineDir, 1.0 * LAB_PROP_K);
    const ob = CINEMATICS[active.id]?.fx?.originBone;
    const bo = ob ? boneByName.get(ob) : undefined;
    if (bo) bo.getWorldPosition(cineOrigin);
    else cineOrigin.set(0, 1.0, 0);
    fxRunner.update(cineU, dt * speed, tip, cineOrigin);
  }
  fx.update(dt);
  controls.update();
  renderer.render(scene, camera);
  requestAnimationFrame(frame);
}

buildRoster();
resize();
void loadFighter(ROSTER[0]);
frame();
